import { useMutation, useQuery } from '@tanstack/react-query';
import { api } from '../services/api';
import { CalculatorParams, AnalysisResponse } from '../types/api';

// Get default parameters
export const useDefaults = () => {
  return useQuery({
    queryKey: ['defaults'],
    queryFn: api.getDefaults,
    staleTime: Infinity, // These don't change often
  });
};

// Run analysis with yearly data and timeout
export const useAnalysis = () => {
  return useMutation<AnalysisResponse, Error, CalculatorParams>({
    mutationFn: async (params) => {
      // Phase 4.1: Log backend requests and responses
      const requestId = Date.now();
      console.log('[API_REQUEST]', { 
        requestId, 
        params: JSON.stringify(params).substring(0, 200),
        type: 'standard'
      });
      
      // Add timeout to prevent hanging
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error('Analysis timeout after 30 seconds')), 30000);
      });

      const response = await Promise.race([
        api.analyze(params, true),
        timeoutPromise
      ]);
      
      console.log('[API_RESPONSE]', { 
        requestId, 
        finalBuyingNW: response.final_buying_net_worth,
        finalRentingNW: response.final_renting_net_worth,
        type: 'standard'
      });
      
      return response;
    },
    retry: false, // Disable retries to prevent infinite loops
  });
};

// Run baseline analysis with yearly data and timeout
export const useBaselineAnalysis = () => {
  return useMutation<AnalysisResponse, Error, {scenarioParams: CalculatorParams, baselineParams: CalculatorParams}>({
    mutationFn: async ({scenarioParams, baselineParams}) => {
      // Phase 4.1: Log backend requests and responses
      const requestId = Date.now();
      console.log('[API_REQUEST]', { 
        requestId, 
        scenarioParams: JSON.stringify(scenarioParams).substring(0, 200),
        baselineParams: JSON.stringify(baselineParams).substring(0, 200),
        type: 'baseline'
      });
      
      // Add timeout to prevent hanging
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error('Analysis timeout after 30 seconds')), 30000);
      });

      // Use the baseline analysis endpoint that compares scenario against baseline
      const response = await Promise.race([
        api.analyzeWithBaseline(scenarioParams, baselineParams, true),
        timeoutPromise
      ]);
      
      console.log('[API_RESPONSE]', { 
        requestId, 
        finalBuyingNW: response.final_buying_net_worth,
        finalRentingNW: response.final_renting_net_worth,
        type: 'baseline'
      });
      
      return response;
    },
    retry: false, // Disable retries to prevent infinite loops
  });
};

// Health check
export const useHealthCheck = () => {
  return useQuery({
    queryKey: ['health'],
    queryFn: api.healthCheck,
    retry: 3,
    retryDelay: 1000,
  });
};
